import { graphql } from 'gatsby';
import React, { useState } from 'react';
import { navigate } from '@reach/router';
import Box from '../../components/Box/Box';
import Tooltip from '../../components/Tooltip/Tooltip';
import {
    Body1,
    Head3,
    Head6,
    Headline,
} from '../../components/Typography/Typography';
import WithSection from '../../components/WithSection/WithSection';
import CheckMarkIcon from '../../images/icons/check-mark.svg';
import MinusIcon from '../../images/icons/minus.svg';
import InfoIcon from '../../images/icons/info.svg';
import PlusIcon from '../../images/icons/plus.svg';
import RemoveIcon from '../../images/icons/remove.svg';
import useViewport from '../../hooks/useViewport';
import { css } from 'styled-components';
import { theme } from '../../../config/theme';
import { rem } from 'polished';
import PrismicLink from '../../components/PrismicLink/PrismicLink';
import Button from '../../components/Button/Button';
import Image from '../../components/Image/Image';
import ChevronRight from '../../images/icons/chevron-right.svg';

const CompareSection = ({ data: { primary, items }, id }) => {
    const [expanded, setExpanded] = useState(false);
    const { isDesktop } = useViewport();
    const {
        column_1,
        column_2,
        column_4,
        column_3,
        column_1_logo,
        column_2_logo,
        column_3_logo,
        column_4_logo,
        featured_column_title,
        heading,
        subheading,
        cta_1_link,
        cta_1_text,
        cta_1_variant,
        cta_2_link,
        cta_2_text,
        cta_2_variant,
        cta_3_link,
        cta_3_text,
        cta_3_variant,
        cta_4_link,
        cta_4_text,
        cta_4_variant,
    } = primary;
    const displaySeeAllFeaturesButton = false;
    const numOfColumns = [
        column_1 || column_1_logo.url,
        column_2 || column_2_logo.url,
        column_4 || column_3_logo.url,
        column_3 || column_4_logo.url,
    ].filter(Boolean).length;

    const gridTemplateColumnsOfTheTableRow = {
        _: `minmax(150px, 800px) repeat(${numOfColumns}, 90px)`,
        lg: `minmax(150px, 800px) repeat(${numOfColumns}, 200px)`,
    };
    const gridColumnGapOfTheTableRow = {
        _: rem(10),
        lg: rem(20),
    };

    const hideSectionInScreensUnderDesktop = !isDesktop && numOfColumns > 2;
    const CTAs = [
        { cta_1_link, cta_1_text, cta_1_variant },
        { cta_2_link, cta_2_text, cta_2_variant },
        { cta_3_link, cta_3_text, cta_3_variant },
        { cta_4_link, cta_4_text, cta_4_variant },
    ];

    return (
        <Box
            display={hideSectionInScreensUnderDesktop ? 'none' : 'block'}
            gridColumn={{ _: '1/-1', lg: '2/-2' }}
        >
            <Box mb={rem(32)}>
                <Head3 textAlign="center" mb={rem(20)}>
                    {heading.text}
                </Head3>
                <Body1 textAlign="center">{subheading}</Body1>
            </Box>
            <Box
                as="table"
                width="100%"
                marginBottom="45px"
                position="relative"
            >
                <Box
                    as="thead"
                    position="sticky"
                    top="var(--header-height)"
                    zIndex="1"
                >
                    <Box
                        as="tr"
                        px={rem(8)}
                        py={rem(16)}
                        display="grid"
                        gridAutoFlow="column"
                        gridTemplateColumns={gridTemplateColumnsOfTheTableRow}
                        gridColumnGap={gridColumnGapOfTheTableRow}
                        bg="neutral.white"
                        height={rem(32)}
                        boxSizing="content-box"
                    >
                        {[
                            featured_column_title,
                            column_1 || column_1_logo,
                            column_2 || column_2_logo,
                            column_3 || column_3_logo,
                            column_4 || column_4_logo,
                        ]
                            .filter((item) =>
                                Boolean(typeof item === 'string' || item.url)
                            )
                            .map(
                                (column, i) =>
                                    column && (
                                        <Box
                                            key={i}
                                            as="th"
                                            display="inline-flex"
                                            alignItems="center"
                                            justifyContent={
                                                i === 0
                                                    ? 'flex-start'
                                                    : 'center'
                                            }
                                            {...(i === 0 && { flex: 1 })}
                                        >
                                            {typeof column === 'object' ? (
                                                <Image
                                                    img={column}
                                                    height={rem(32)}
                                                />
                                            ) : (
                                                <Head6>{column}</Head6>
                                            )}
                                        </Box>
                                    )
                            )}
                    </Box>
                </Box>
                <Box as="tbody" display="block">
                    <Box
                        height={
                            !(displaySeeAllFeaturesButton && expanded)
                                ? '100%'
                                : '590px'
                        }
                        overflow={
                            !(displaySeeAllFeaturesButton && expanded)
                                ? 'initial'
                                : 'hidden'
                        }
                    >
                        {items.map(
                            (
                                {
                                    feature_title,
                                    is_group_title,
                                    is_in_column_1,
                                    is_in_column_2,
                                    is_in_column_3,
                                    is_in_column_4,
                                    tooltip_text,
                                },
                                i
                            ) => {
                                const tooltipContent = tooltip_text && (
                                    <Box
                                        whiteSpace="nowrap"
                                        display="inline-flex"
                                        verticalAlign="middle"
                                        cursor="help"
                                        mt={rem(-2)}
                                    >
                                        <Tooltip
                                            content={
                                                <Tooltip.Content>
                                                    {tooltip_text}
                                                </Tooltip.Content>
                                            }
                                        >
                                            <Box
                                                color="neutral.1100"
                                                as={InfoIcon}
                                                size={20}
                                                ml={rem(4)}
                                            />
                                        </Tooltip>
                                    </Box>
                                );
                                // @ts-ignore
                                return is_group_title ? (
                                    <Box
                                        key={i}
                                        as="tr"
                                        textAlign="left"
                                        display="block"
                                        mb={rem(16)}
                                        bg="neutral.white"
                                        position="sticky"
                                        top="calc(var(--header-height) + 64px)"
                                    >
                                        <Box as="th" padding={rem(8)}>
                                            <Headline fontWeight={500}>
                                                {feature_title}
                                            </Headline>
                                        </Box>
                                    </Box>
                                ) : (
                                    // @ts-ignore
                                    <Box
                                        key={feature_title}
                                        as="tr"
                                        display="grid"
                                        gridAutoFlow="column"
                                        gridTemplateColumns={
                                            gridTemplateColumnsOfTheTableRow
                                        }
                                        gridColumnGap={
                                            gridColumnGapOfTheTableRow
                                        }
                                        alignItems="center"
                                        css={css`
                                            + * {
                                                margin-top: ${rem(10)};
                                            }
                                        `}
                                        _hover={{
                                            backgroundColor:
                                                theme.colors.primary[50],
                                        }}
                                        padding={rem(8)}
                                    >
                                        <Box
                                            as="td"
                                            flex={1}
                                            display="flex"
                                            alignItems="center"
                                        >
                                            <Box as={Body1}>
                                                {feature_title
                                                    .split(' ')
                                                    .slice(0, -1)
                                                    .join(' ')}{' '}
                                                <span>
                                                    {feature_title
                                                        .split(' ')
                                                        .pop()}
                                                    {tooltipContent}
                                                </span>
                                            </Box>
                                        </Box>
                                        {[
                                            is_in_column_1,
                                            is_in_column_2,
                                            is_in_column_3,
                                            is_in_column_4,
                                        ]
                                            .slice(0, numOfColumns)
                                            .map((isInColumn, k) => (
                                                <Box
                                                    key={k}
                                                    as="td"
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                >
                                                    {isInColumn ? (
                                                        <Box
                                                            color="primary.main"
                                                            as={CheckMarkIcon}
                                                            size={32}
                                                        />
                                                    ) : (
                                                        <Box
                                                            color="neutral.main"
                                                            as={MinusIcon}
                                                            size={32}
                                                        />
                                                    )}
                                                </Box>
                                            ))}
                                    </Box>
                                );
                            }
                        )}
                    </Box>
                </Box>
                <Box as="tfoot">
                    <Box
                        height={rem(54)}
                        as="tr"
                        display="grid"
                        gridAutoFlow="column"
                        gridTemplateColumns={gridTemplateColumnsOfTheTableRow}
                        gridColumnGap={gridColumnGapOfTheTableRow}
                        padding={rem(8)}
                        mt={rem(8)}
                    >
                        {cta_4_text ? (
                            <Box as="td">
                                <PrismicLink link={cta_4_link}>
                                    <Button
                                        variant={cta_4_variant}
                                        size="md"
                                        minWidth={{ _: 'auto', lg: rem(134) }}
                                        endIcon={
                                            <Box
                                                as={ChevronRight}
                                                size={rem(20)}
                                                color="currentColor"
                                            />
                                        }
                                    >
                                        {cta_4_text}
                                    </Button>
                                </PrismicLink>
                            </Box>
                        ) : (
                            <Box as="td" />
                        )}
                        {CTAs.slice(0, numOfColumns).map((cta, cta_i) => (
                            <Box
                                key={cta_i}
                                as="td"
                                display="flex"
                                justifyContent="center"
                                alignItems="center"
                            >
                                {Boolean(cta[`cta_${cta_i + 1}_text`]) ? (
                                    <PrismicLink
                                        link={cta[`cta_${cta_i + 1}_link`]}
                                    >
                                        <Button
                                            variant={
                                                cta[`cta_${cta_i + 1}_variant`]
                                            }
                                            size="md"
                                            minWidth={{
                                                _: 'auto',
                                                lg: rem(134),
                                            }}
                                        >
                                            {cta[`cta_${cta_i + 1}_text`]}
                                        </Button>
                                    </PrismicLink>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
            {displaySeeAllFeaturesButton && (
                <Box textAlign="center">
                    <Button
                        size="md"
                        variant="tertiary"
                        // @ts-ignore
                        onClick={() => {
                            setExpanded(!expanded);
                            navigate(`#${id}`, { replace: false });
                        }}
                        endIcon={
                            <Box
                                as={expanded ? RemoveIcon : PlusIcon}
                                size={rem(16)}
                            />
                        }
                    >
                        {expanded ? 'See less features' : 'See more features'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export const _ = graphql`
    fragment COMPARE_PLAN_SECTION on PrismicSectionDataBodyComparePlanSection {
        id
        primary {
            column_1
            column_2
            column_4
            column_3
            column_1_logo {
                url
            }
            column_2_logo {
                url
            }
            column_4_logo {
                url
            }
            column_3_logo {
                url
            }
            featured_column_title
            heading {
                text
            }
            subheading
            cta_1_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_1_text
            cta_1_variant
            cta_2_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_2_text
            cta_2_variant
            cta_3_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_3_text
            cta_3_variant
            cta_4_link {
                ...PRISMIC_LINK_FRAGMENT
            }
            cta_4_text
            cta_4_variant
        }
        items {
            feature_title
            is_group_title
            is_in_column_1
            is_in_column_2
            is_in_column_3
            is_in_column_4
            tooltip_text
        }
    }
`;
export default WithSection(CompareSection);
